import type { CanvasNode } from '../types';
import type { LibraryWorkspace } from './workspaceLibrary';
import type { WorkspaceFolder } from './workspaceFolders';
import { getFolderPath } from './folderTree';
import { collectReferenceNodes, isReferenceNode, stableMediaKey, type ReferenceChoice } from './workspaceEdit';
import { isLocalMediaUrl } from './desktopFolder';

/**
 * Reference nodes are shared across a project: the Image and Video nodes of one workspace can
 * use the Reference nodes of the project's other workspaces, by link, so a character or a set
 * is made once and not copied into every workspace. A project is a top-level folder of the
 * library; its workspaces are the ones filed anywhere under it.
 */

export interface ProjectScope {
    projectId: string;
    projectName: string;
    /** The project's other workspaces, newest saved first. */
    siblings: LibraryWorkspace[];
}

/** The project the open workspace is filed under, and the other workspaces in it; null outside a project. */
export const projectScope = (openWorkspaceId: string | null, workspaces: LibraryWorkspace[], folders: WorkspaceFolder[]): ProjectScope | null => {
    if (!openWorkspaceId) return null;
    const open = workspaces.find(w => w.id === openWorkspaceId);
    const project = open?.folderId ? getFolderPath(folders, open.folderId)[0] : undefined;
    if (!open || !project) return null;
    const siblings = workspaces
        // The project's own workspaces; in a project shared with this person, those shared with it
        .filter(w => w.id !== openWorkspaceId && (!w.isShared || w.sharedProjectId === project.id) && w.folderId && getFolderPath(folders, w.folderId)[0]?.id === project.id)
        .sort((a, b) => b.lastSaved - a.lastSaved);
    return { projectId: project.id, projectName: project.name, siblings };
};

/** One other workspace's Reference nodes, as last read. */
export interface SharedSource {
    workspaceId: string;
    workspaceName: string;
    nodes: CanvasNode[];
}

/**
 * The project's other Reference nodes as list choices. Media the open workspace already has
 * as a Reference node is offered once, from here; media several other workspaces have is
 * offered once, from the most recently saved of them. Session-only addresses are left out.
 */
export const sharedReferenceChoices = (sources: SharedSource[], here: ReferenceChoice[]): ReferenceChoice[] => {
    const keyOf = (url: string) => stableMediaKey(url) ?? url;
    const seen = new Set(here.map(choice => keyOf(choice.url)));
    const out: ReferenceChoice[] = [];
    for (const source of sources) {
        for (const choice of collectReferenceNodes(source.nodes, { workspaceId: source.workspaceId, workspaceName: source.workspaceName })) {
            if (!choice.url || /^blob:/i.test(choice.url) || isLocalMediaUrl(choice.url)) continue;
            const key = keyOf(choice.url);
            if (seen.has(key)) continue;
            seen.add(key);
            out.push(choice);
        }
    }
    return out;
};

export type ReadReferenceNodes = (workspace: LibraryWorkspace) => Promise<CanvasNode[]>;

const cache = new Map<string, { lastSaved: number; nodes: CanvasNode[] }>();

/**
 * Reads the Reference nodes of `workspaces`, a few at a time; a workspace not saved since it
 * was last read comes from memory. A workspace that cannot be read is skipped this time.
 */
export const readSharedSources = async (workspaces: LibraryWorkspace[], read: ReadReferenceNodes): Promise<SharedSource[]> => {
    const results: SharedSource[] = [];
    const queue = [...workspaces];
    const worker = async () => {
        for (let workspace = queue.shift(); workspace; workspace = queue.shift()) {
            const kept = cache.get(workspace.id);
            let nodes: CanvasNode[];
            if (kept && kept.lastSaved === workspace.lastSaved) {
                nodes = kept.nodes;
            } else {
                try {
                    nodes = (await read(workspace)).filter(isReferenceNode);
                    cache.set(workspace.id, { lastSaved: workspace.lastSaved, nodes });
                } catch (e) {
                    console.warn(`[projectReferences] Could not read the references of "${workspace.name}":`, e);
                    continue;
                }
            }
            results.push({ workspaceId: workspace.id, workspaceName: workspace.name, nodes });
        }
    };
    await Promise.all(Array.from({ length: Math.min(4, workspaces.length) }, worker));
    // In the order given (newest saved first), however the reads finished
    const order = new Map(workspaces.map((w, i) => [w.id, i]));
    return results.sort((a, b) => (order.get(a.workspaceId) ?? 0) - (order.get(b.workspaceId) ?? 0));
};

/** Forgets what was read, so the next read asks again (for tests, and after signing out). */
export const forgetSharedSources = (): void => cache.clear();
